﻿import ENUMS from '@core/classes/Enums';
import LOCALIZATION from '@core/classes/Localization';
import util from '@core/services/util';
import { formatDateToString, getDateInstance } from '@core/services/util.date';
import { differenceInMonths } from 'date-fns';
import { es } from 'date-fns/locale/es';
import { reactive } from 'vue';

import * as applicationSettingsAPIs from '@core/classes/Applications/ApplicationsSettingsAPIs';
import * as applicationSettingsDropdowns from '@core/classes/Applications/ApplicationsSettingsDropdowns';
import ApplicationsSettingsLoader from '@core/classes/Applications/ApplicationsSettingsLoader';
import CustomerApplicationHelper from '@core/helpers/customer-application-helper';
import modalEditApplication from '@core/modals/customerApp/modalEditApplication.vue';
import $modal from '@core/services/modal';
import { EventBusCore } from '@core/services/util';

export default class ApplicationsQuestion {
	constructor(init) {
		this.customerApp = () => init.customerApp;
		this.key = init.question.key;
		this.dateType = init.question.dateType;
		this.mask = init.question.mask;
		this.type = init.question.type;
		this.triggerSave = init.question.triggerSave;
		this.category = init.question.category;
		this.dataStructurePath = init.question.dataStructurePath;
		this.answerButtons = init.question.answerButtons;
		this.summaryDisplay = init.question.summaryDisplay;
		this.isEndOfApp = !!init.question.isEndOfApp;
		this.isCompleted = !!init.question.isCompleted;
		this.savedAnswer = init.question.savedAnswer;
		this.cssClass = init.question.cssClass;
		this.dropdown = init.question.dropdown;
		this.textsToShow = init.question.textsToShow;
		this.notesToShow = init.question.notesToShow;
		this.prevQuestion = init.question.prevQuestion;
		this.inputType = init.question.inputType;
		this.onKeyPressMethod = init.question.onKeyPressMethod;
		this.atInputMethod = init.question.atInputMethod;
		this.beforeContinueAction = init.question.beforeContinueAction;
		this.minLength = null;
		this.maxLength = null;
		this.GetValidation = () => this.getValidators(init.question);
		this.redirectToPlaid = init.question.redirectToPlaid;
		this.redirectToCustomerPortal = init.question.redirectToCustomerPortal;
		this.isFirstInGroup = init.question.isFirstInGroup;
		this.redoQuestions = init.question.redoQuestions;
	}

	ApplicationsSettings() {
		return ApplicationsSettingsLoader.ApplicationSettings;
	}

	getOnKeyPressMethod() {
		let methods = [];
		if (this.onKeyPressMethod != null && this.onKeyPressMethod.length > 0) {
			methods = this.onKeyPressMethod?.filter(k => k.conditionToUse == null || this.checkConditions(k.conditionToUse));
		}

		if (methods.length > 0) {
			return methods[0].methodName;
		}
	}

	getAtInputMethod() {
		let methods = [];
		if (this.atInputMethod != null && this.atInputMethod.length > 0) {
			methods = this.atInputMethod?.filter(k => k.conditionToUse == null || this.checkConditions(k.conditionToUse));
		}

		if (methods.length > 0) {
			return methods[0].methodName;
		}
	}

	getValidators(question) {
		let validation = [];
		if (question.validation) {
			validation = question.validation.filter(v => v.conditionToUse == null || this.checkConditions(v.conditionToUse));
			this.minLength = validation?.find(v => v.propertyName == 'textValue')?.validators.find(vs => vs.type == 'minLength')?.params[0];
			this.maxLength = validation?.find(v => v.propertyName == 'textValue')?.validators.find(vs => vs.type == 'maxLength')?.params[0];
		}
		return util.getValidators([{ validation }]);
	}

	isValidationsMet(answer, validation) {
		if (!answer.isValidationRequired || (answer.isValidationRequired && !validation)) {
			return true;
		}

		return !validation.$invalid;
	}

	saveAnswerFunc(value) {
		//updates the real model with the answer
		this.setValue(value);

		//for every question, we store any answer given in this.savedAnswer even if you go back to the previous question
		this.savedAnswer = value;
	}

	saveMultiAnswerFunc(extraValue, saveExtraValuesTo) {
		this.setValue(extraValue, saveExtraValuesTo);
	}

	isDate() {
		return this.dateType != null;
	}
	showDay() {
		return this.dateType == 'day';
	}
	showMonth() {
		return this.dateType == 'month' || this.dateType == 'day';
	}

	//creates the actions that will happen when you answer this question
	getAnswers() {
		const allAnswers = reactive([]);
		this.answerButtons.forEach(ab => {
			const labelToUse = ab.label ?? this.getValue(ab.labelPath);
			allAnswers.push({
				label: labelToUse,
				style: ab.style,
				isValidationRequired: ab.isValidationRequired,
				actions: async (value = true, extraSaves, question, toggleLoading, saveApplicationAction) => {
					const saveActionIndex = ab.actions.findIndex(a => a.action === ENUMS.CREDIT_APP_BUTTONS_ACTIONS.SAVE_ANSWER);
					if (saveActionIndex >= 0) {
						//pre save
						const preSaveActions = ab.actions.slice(0, saveActionIndex);
						await this.executeActions(preSaveActions, labelToUse, value, extraSaves, question, toggleLoading, saveApplicationAction);

						//save action
						const saveAction = ab.actions[saveActionIndex];
						let valueToSave = value;

						if (saveAction.overrides?.length > 0) {
							const override = saveAction.overrides.find(({ conditionList }) => conditionList == null || this.checkConditions(conditionList));

							if (override) {
								valueToSave = this.getOverrideValue(override);
							}
						}

						this.openModalEditApplication(valueToSave, question, async () => {
							//post save
							this.saveAnswerFunc(valueToSave);
							const postSaveActions = ab.actions.slice(saveActionIndex + 1);
							await this.executeActions(postSaveActions, labelToUse, value, extraSaves, question, toggleLoading, saveApplicationAction);
						});
					} else {
						await this.executeActions(ab.actions, labelToUse, value, extraSaves, question, toggleLoading, saveApplicationAction);
					}
				},
			});
		});

		return allAnswers;
	}

	async executeActions(actions, label, value, extraSaves, question, toggleLoading, saveApplicationAction) {
		const shouldToggleLoading = actions.some(x => {
			switch (x.action) {
				case ENUMS.CREDIT_APP_BUTTONS_ACTIONS.SAVE_FULL_APPLICATION:
				case ENUMS.CREDIT_APP_BUTTONS_ACTIONS.CALL_API:
					return true;
				default:
					return false;
			}
		});

		if (shouldToggleLoading) toggleLoading();

		for (const act of actions) {
			switch (act.action) {
				case ENUMS.CREDIT_APP_BUTTONS_ACTIONS.NEXT_QUESTION: {
					this.setNextQuestion(this.getNextQuestion(act, label), !(label == 'noSummary' || this.redoQuestions) /*, act, saveApplicationAction*/);
					if (act.saveObj?.triggerSave && (act.saveObj.conditionsToSave == null || this.checkConditions(act.saveObj.conditionsToSave))) {
						await new Promise(resolve => {
							setTimeout(async () => {
								await saveApplicationAction(true);
								resolve();
							}, 400);
						});
					}
					this.redoQuestions = false;
					break;
				}
				case ENUMS.CREDIT_APP_BUTTONS_ACTIONS.SAVE_MULTI_ANSWER: {
					act.extraValuesToSave.forEach((e, index) => {
						if (e.type == 'value' && e.override) {
							this.saveMultiAnswerFunc(this.getOverrideValue(e.override), e.savePath);
							//Structure to use in ApplicationSettings for this case (currently not being used)
							//extraValuesToSave: [
							//    {
							//        type: "value",
							//        savePath: 'quoteAppData.applicationsDataAnswered.personalInfo.currentResidence.zipCode',
							//        override: {
							//            value: 'quoteAppData.applicationsDataAnswered.tempValue',
							//            type: 'path'
							//        }
							//    },
							//],
						} else if (extraSaves.length != 0) {
							if (e.type == 'value') {
								this.saveMultiAnswerFunc(extraSaves[index], e.savePath);
							} else {
								e.properties.forEach(p => this.saveMultiAnswerFunc(extraSaves[index][p.property], p.savePath));
								//Structure to use in ApplicationSettings for this case (currently not being used)
								//extraValuesToSave: [
								//    {
								//        type: "object",
								//        properties: [
								//            {
								//                property: "ssnHashed",
								//                savePath: 'quoteAppData.applicationsDataAnswered.personalInfo.ssnHashed'
								//            },
								//        ]
								//    },
								//],
							}
						}
					});
					break;
				}
				case ENUMS.CREDIT_APP_BUTTONS_ACTIONS.CALL_API: {
					//timeout can be used to compensate for the animation time (0.3s) and some extras
					await new Promise(resolve => {
						setTimeout(async () => {
							await applicationSettingsAPIs[act.apiMethod](value, extraSaves, question, this.customerApp());
							resolve();
						}, 400);
					});
					break;
				}
				case ENUMS.CREDIT_APP_BUTTONS_ACTIONS.SAVE_FULL_APPLICATION: {
					if (act.conditionsToSave == null || this.checkConditions(act.conditionsToSave)) {
						await new Promise(resolve => {
							setTimeout(async () => {
								await saveApplicationAction(true);
								resolve();
							}, 400);
						});
					}
					break;
				}
				default:
			}
		}

		if (shouldToggleLoading) {
			setTimeout(async () => {
				toggleLoading();
			}, 400);
		}
	}

	openModalEditApplication(newValue, question, postFunction) {
		let { key, savedAnswer } = question;
		const application = this.customerApp();

		//if date, update format for comparison
		if (this.isDate()) {
			savedAnswer = formatDateToString(savedAnswer);
		}

		//if completed, the values are different, and the old one was not null(different path), user needs to justify change
		if (application.isCompleted() && savedAnswer != null && `${newValue}` != `${savedAnswer}`) {
			$modal.open(modalEditApplication, {
				name: 'modalEditApplication',
				passedData: { key, savedAnswer, newValue, application },
				backdrop: true,
				postFunction,
			});
		} else {
			postFunction();
		}
	}

	//for every question, we store any answer given in this.savedAnswer even if you go back to the previous question
	//this function recalls the last given answer for this question (that we stored)
	getSavedAnswer() {
		return this.savedAnswer;
	}

	getNextQuestion(act, label) {
		let nextQuestion = act.nextQuestion;

		if (this.customerApp().plaidId) {
			const prefilledReplacement = this.ApplicationsSettings().prefilledReplacements.find(r => r.useQuestionKey === nextQuestion);
			if (prefilledReplacement) {
				nextQuestion = prefilledReplacement.nextQuestionKey;
			}
		}

		if (act.conditions?.length > 0) {
			const condition = act.conditions.find(({ conditionList }) => this.checkConditions(conditionList));

			if (condition) {
				nextQuestion = condition.goTo;
			}
		}

		//If question is summary or redoQuestions is true, it is removed from the path
		if ((this.type.includes('Summary') && label == 'noSummary') || this.redoQuestions) {
			let complete = false;
			for (var i = this.customerApp().quoteAppData.pathFollowed.length - 1; i >= 0; i--) {
				if (!complete) {
					const questionDeleted = this.customerApp().quoteAppData.pathFollowed.pop();
					if (questionDeleted == nextQuestion) {
						complete = true;
					}
				}
			}
		}

		return nextQuestion;
	}

	checkConditions(conditionList) {
		return conditionList?.every(({ dataPath, dataPathType, comparisonType, value, valueType, isValueInsideQuestion }) => {
			let savedValue = this.getValue(dataPath, isValueInsideQuestion);
			let valueToCompare = valueType == 'path' ? this.getValue(value) : value;

			//Values Transformation for the edge DATE case
			if (dataPathType == 'date') {
				savedValue = differenceInMonths(getDateInstance(), getDateInstance(savedValue));
				valueToCompare = parseInt(valueToCompare);
			}

			if (dataPathType == 'array') {
				if (comparisonType == 'equal') {
					return savedValue.some(s => this.compareValues(comparisonType, s, valueToCompare));
				} else {
					return savedValue.every(s => this.compareValues(comparisonType, s, valueToCompare));
				}
			} else {
				return this.compareValues(comparisonType, savedValue, valueToCompare);
			}
		});
	}

	compareValues(comparisonType, value, valueToCompare) {
		if (comparisonType == 'equal' && value == valueToCompare) {
			return true;
		} else if (comparisonType == 'greaterOrEqual' && value >= valueToCompare) {
			return true;
		} else if (comparisonType == 'lessOrEqual' && value <= valueToCompare) {
			return true;
		} else if (comparisonType == 'greater' && value > valueToCompare) {
			return true;
		} else if (comparisonType == 'less' && value < valueToCompare) {
			return true;
		} else if (comparisonType == 'different' && value != valueToCompare) {
			return true;
		} else {
			return false;
		}
	}

	/**
	 * Takes the user to the next question in the quote application.
	 * Triggers an event when changing the current question to the next.
	 *
	 * This method handles navigation to the next question and emits an event
	 * to notify components about the question change.
	 *
	 * @param {*} nextQuestion The next question to navigate to.
	 * @param {boolean} addToPathFollow Whether to add the current question to the path followed (default: true).
	 *
	 * @remarks
	 * - currentQuestion: Represents the current question before navigating to the next one.
	 * - nextQuestion: Represents the next question that the user navigates to.
	 * - addToPathFollow: Indicates whether to add the current question to the path followed.
	 */
	setNextQuestion(nextQuestion, addToPathFollow = true /*, act, saveApplicationAction*/) {
		const app = this.customerApp();
		const currentQuestion = app.quoteAppData.currentQuestion;

		// If the application is being edit in single question mode, the next question will be set to the edit thank you page
		if (app.isSingleQuestionEdit == true) {
			nextQuestion = 'applicationUpdated';
		}

		// Add the current question to the path followed if addToPathFollow is true
		if (addToPathFollow) {
			app.quoteAppData.pathFollowed.push(currentQuestion);
		}

		// Update the current question to the next question
		app.quoteAppData.currentQuestion = nextQuestion;

		//// Need the save here
		//if (act.saveObj?.triggerSave && (act.saveObj.conditionsToSave == null || this.checkConditions(act.saveObj.conditionsToSave))) {
		//    await new Promise((resolve) => {
		//        setTimeout(async () => {
		//            await saveApplicationAction(true);
		//            resolve()
		//        }, 400)
		//    })
		//}

		// Emit an event indicating the navigation to the next question
		EventBusCore.emit('goToNextQuestion', currentQuestion, nextQuestion); //FIXME: Don't manipulate components from non-plugins
	}

	/**
	 * Takes the user to the previous question in the quote application.
	 * Triggers an event when changing the current question to the previous.
	 *
	 * This method handles navigation to the previous question and emits an event
	 * to notify components about the question change.
	 *
	 * @remarks
	 * - currentQuestion: Represents the current question before navigating to the previous one.
	 * - upcomingQuestion: Represents the previous question that the user navigates back to,
	 * retrieved from the path followed.
	 */
	getPreviousQuestion() {
		const app = this.customerApp();
		const currentQuestion = app.quoteAppData.currentQuestion;

		// Check if there's a previous question available in the path followed
		if (app.quoteAppData.pathFollowed.length > 0) {
			// Pop the last question from the path followed as the upcoming question
			const upcomingQuestion = app.quoteAppData.pathFollowed.pop();

			// Reset any necessary state or values (not specified in the original code)
			this.setValue(null);

			// Set the current question to the upcoming question (previous question)
			app.quoteAppData.currentQuestion = upcomingQuestion;

			// Emit an event indicating the navigation to the previous question
			EventBusCore.emit('goToPreviousQuestion', currentQuestion, upcomingQuestion); //FIXME: Don't manipulate components from non-plugins
		}
	}

	//returns the text for this question
	//substitutes place holders ({dataPath}) with answers based on THAT question's answer
	//ex: "Hello, {firstNameQuestionKey}" => Hello, Simcha

	doesQuestionHasValue(text) {
		const questionValue = this.getValue(this.customerApp().quoteAppData.questions.find(q => q.key == text.conditionToShow).dataStructurePath);
		return questionValue && (!Array.isArray(questionValue) || (Array.isArray(questionValue) && questionValue.length > 0));
	}

	getQuestion() {
		const output = [];
		this.textsToShow
			.filter(t => t.conditionToShow == null || this.checkConditions(t.conditionToShow))
			.forEach(text => {
				let question = text[this.customerApp().quoteAppData.language];

				question = CustomerApplicationHelper.getPrefilledData(
					this.ApplicationsSettings().prefilledReplacements,
					this.customerApp().preFilledData,
					question,
					this.customerApp().quoteAppData.language,
				);

				this.customerApp().quoteAppData.questions.forEach(q => {
					const formattedKey = '{' + q.key + '}';
					while (question.includes(formattedKey)) {
						if (this.getValue(q.dataStructurePath) == null) {
							question = question.replace(formattedKey, '');
						} else {
							question = question.replace(formattedKey, q.getFormatToDisplay(q.getValue(q.dataStructurePath), this.customerApp().quoteAppData.language));
						}
					}
				});
				output.push(question);
			});

		return output;
	}

	getNotes() {
		const output = [];

		this.notesToShow
			?.filter(t => t.conditionToShow == null || this.checkConditions(t.conditionToShow))
			.forEach(text => {
				let notes = text[this.customerApp().quoteAppData.language];
				notes = CustomerApplicationHelper.getPrefilledData(
					this.ApplicationsSettings().prefilledReplacements,
					this.customerApp().preFilledData,
					notes,
					this.customerApp().quoteAppData.language,
				);

				this.customerApp().quoteAppData.questions.forEach(q => {
					const formattedKey = '{' + q.key + '}';
					while (notes.includes(formattedKey)) {
						if (this.getValue(q.dataStructurePath) == null) {
							notes = notes.replace(formattedKey, '');
						} else {
							notes = notes.replace(formattedKey, q.getFormatToDisplay(q.getValue(q.dataStructurePath), this.customerApp().quoteAppData.language));
						}
					}
				});
				output.push(notes);
			});

		return output;
	}

	getFormatToDisplay(formatToDisplay, language) {
		if (this.type == 'QuestionDate') {
			switch (this.dateType) {
				case 'year':
					formatToDisplay = formatDateToString(formatToDisplay, { formatString: 'yyyy', locale: es });
					break;
				case 'month':
					if (language == 'Spanish') {
						if (getDateInstance(formatToDisplay).getFullYear() >= 2000) {
							formatToDisplay = formatDateToString(formatToDisplay, { formatString: "MMMM 'del' yyyy", locale: es });
						} else {
							formatToDisplay = formatDateToString(formatToDisplay, { formatString: "MMMM 'de' yyyy", locale: es });
						}
					} else {
						formatToDisplay = formatDateToString(formatToDisplay, { formatString: 'MMMM, yyyy' });
					}
					break;
				default:
					if (language == 'Spanish') {
						if (getDateInstance(formatToDisplay).getFullYear() >= 2000) {
							formatToDisplay = formatDateToString(formatToDisplay, { formatString: "d 'de' MMMM 'del' yyyy", locale: es });
						} else {
							formatToDisplay = formatDateToString(formatToDisplay, { formatString: "d 'de' MMMM 'de' yyyy", locale: es });
						}
					} else {
						formatToDisplay = formatDateToString(formatToDisplay, { formatString: 'MMMM do, yyyy' });
					}
					break;
			}
		} else if (this.type == 'QuestionInputDropdown') {
			formatToDisplay = this.getDropdownsDisplay(formatToDisplay, language);
		} else if (this.type == 'QuestionMultiButtons') {
			formatToDisplay = this.getLocalizationDisplay(formatToDisplay, language);
		} else if (this.type == 'QuestionInputPhone') {
			formatToDisplay = this.formatPhone(formatToDisplay);
		} else if (this.type == 'QuestionSocial') {
			formatToDisplay = this.formatSocial(formatToDisplay);
		} else if (this.type == 'QuestionInputCurrency') {
			formatToDisplay = this.formatCurrency(formatToDisplay);
		} else if (this.type == 'QuestionMultiSelect') {
			let joinText = null;
			if (this.key == 'rebateOrganizations') {
				joinText = language == 'English' ? ' <small>and</small> ' : ' <small>y</small> ';
			} else {
				joinText = language == 'English' ? ' <small>or</small> ' : ' <small>o</small> ';
			}
			formatToDisplay = formatToDisplay.map(item => this.getLocalizationDisplay(item, language)).join(joinText);
		}

		return formatToDisplay;
	}

	getDropdownsDisplay(formatToDisplay, language) {
		if (!this.dropdown.displayValue) {
			const dropdown = applicationSettingsDropdowns[this.dropdown.listMethod](this.dropdown.keys, language, this.customerApp, () => {});
			const answer = dropdown.find(d => d.value == formatToDisplay)?.text;
			if (answer) {
				formatToDisplay = answer;
			}
		}
		return formatToDisplay;
	}

	clearAnswer() {
		this.savedAnswer = null;
	}

	getLocalizationDisplay(formatToDisplay, language) {
		let localizationDisplay = formatToDisplay;
		if (LOCALIZATION.buttonLabels[formatToDisplay]) {
			localizationDisplay = LOCALIZATION.buttonLabels[formatToDisplay][language];
		}

		return localizationDisplay;
	}

	formatPhone(value) {
		let valueFormatted = '';
		for (let i = 0; i < value.length; i++) {
			if (i == 0) {
				valueFormatted += '+1 (';
			} else if (i == 3) {
				valueFormatted += ') ';
			} else if (i == 6) {
				valueFormatted += '-';
			}
			valueFormatted += value[i];
		}
		return valueFormatted;
	}
	formatSocial(value) {
		let valueFormatted = '';
		if (this.mask == 'XX-XXXXXXX') {
			for (let i = 0; i < value.length; i++) {
				if (i == 2) {
					valueFormatted += '-';
				}
				valueFormatted += value[i];
			}
		} else {
			for (let i = 0; i < value.length; i++) {
				if (i == 3) {
					valueFormatted += '-';
				} else if (i == 5) {
					valueFormatted += '-';
				}
				valueFormatted += value[i];
			}
		}

		return valueFormatted;
	}
	formatCurrency(value) {
		const valueFormatted = '$' + value.toLocaleString();

		return valueFormatted;
	}

	isQuestionInPath(questionKey) {
		return this.customerApp().quoteAppData.pathFollowed.includes(questionKey);
	}

	getOverrideValue(override) {
		switch (override.type) {
			case 'path':
				return this.getValue(override.value);
			default:
				return override.value;
		}
	}

	//find memory location for last key in save path, and set value to it
	//ex: ("simcha","this.customerApp().quoteAppData.customer.firstName") => this.customerApp().quoteAppData.customer.firstName will be set to "simcha"
	setValue(value, path = null) {
		let object = this.customerApp();
		const pathArray = path ? path.split('.') : this.dataStructurePath.split('.');
		const lastKey = pathArray.pop();

		const updateKey = (property, isLastKey) => {
			if (property.includes('[')) {
				const propertyPieces = property.split('[');

				property = propertyPieces[0];
				object = object[property];
				property = propertyPieces.pop().split(']')[0];

				if (object[property] == null) {
					object.push({});
				}
			}

			if (isLastKey) {
				object[property] = value;
			} else {
				object = object[property];
			}
		};

		pathArray.forEach(p => {
			updateKey(p);
		});

		updateKey(lastKey, true);
	}

	//get value based on string path
	//ex: "this.customerApp().quoteAppData.customer.firstName" will return "simcha"
	getValue(pathString, isValueInsideQuestion = false) {
		console.log('Get Value function: pathString', pathString);
		console.log('Get Value function: isValueInsideQuestion', isValueInsideQuestion);
		if (isValueInsideQuestion) {
			return this[pathString];
		}

		let object = this.customerApp();

		const updateKey = property => {
			if (property.includes('[')) {
				const propertyPieces = property.split('[');

				property = propertyPieces[0];
				object = object[property];
				property = propertyPieces.pop().split(']')[0];
			}

			if (object == null) {
				return null;
			}

			object = object[property];
		};

		pathString.split('.').forEach(p => {
			updateKey(p);
		});

		return object;
	}

	populateSavedAnswer() {
		const savedAnswer = this.getValue(this.dataStructurePath);
		if (savedAnswer) {
			this.savedAnswer = savedAnswer;
		}
	}
}
